import React, { useEffect, useState } from 'react';
import { Box, Button, Menu, MenuItem, Skeleton, TablePagination, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Loader from '../../common/Loader';
import MssDelete from '../../common/MssDelete';
import MssModal from '../../common/MssModal';
import PageBreadcrumbs from '../../common/Breadcrumbs/PageBreadcrumbs';
import BreadcrumbCrumb from '../../common/Breadcrumbs/BreadcrumbCrumb';
import { FormControlledTextField } from '../../common/Form/FormTextField';
import { useIsNarrowScreen } from '../../common/useIsNarrowScreen';
import useResourceDetail from '../../common/useResourceDetail';
import useUnsavedChangesGuard from '../../common/useUnsavedChangesGuard';
import AIResponseError from '../../common/AIResponseError';
import { TruncatedTitle } from '../../common/TruncatedTitle';
import {
  QUIZ_EDITOR_INSTRUCTIONS,
  QUIZ_INCOMPLETE_ERROR,
  QUIZ_QUESTION_TYPE,
  RESOURCE_LABEL,
  RESOURCE_STATUS,
} from '../../common/constants';
import { QuizGenerationStatus } from '../QuizGenerationStatus';
import ResourceInfoPopover from '../../common/ResourceInfoPopover';
import UnsavedChangesWarning from '../../common/UnsavedChangesWarning';
import { QuizMobileBar } from '../QuizMobileBar';
import { QuestionEditor } from './QuestionEditor';
import { EditorQuestionListSheet } from './EditorQuestionListSheet';
import {
  createEmptyQuestion,
  findIncompleteQuestion,
  fromEditableQuestion,
  toEditableQuestion,
} from '../helpers';
import { deleteData, getData, putData } from '../../../DataAccessLayer';
import { quizzes } from '../../../DataAccessLayer/services';

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

function QuizEditor() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const knownQuizTitle = useLocation().state?.title;
  const isImpersonating = useSelector((state) => state.impersonation.isImpersonating);
  const courseId = useSelector((state) => state.course.courseId);
  const showBanner = isImpersonating || !!courseId;

  // The same loader the quiz itself and the deck pages use.
  const {
    resource: quiz,
    setResource: setQuiz,
    isLoading,
    loadError,
  } = useResourceDetail({
    fetch: () => getData(quizzes({ id: quizId, courseId })),
    resourceLabel: 'practice quiz',
    id: quizId,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  // When the editor last saved, which it reports in place of the warning.
  const [savedAt, setSavedAt] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  // Which question a phone is showing, and whether the list of them is open.
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [isQuestionListOpen, setIsQuestionListOpen] = useState(false);
  // Which plus button opened the "what kind of question?" menu, and where its
  // question sits — the new one goes directly below it.
  const [addMenu, setAddMenu] = useState(null);

  const isNarrow = useIsNarrowScreen();

  // Set when the editor has just turned to a page it needs marked up: the
  // fields there are only mounted on the next render, so validating them has to
  // wait for it.
  const [needsValidation, setNeedsValidation] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isDirty },
  } = useForm({ defaultValues: { title: '', questions: [] } });

  /*
   * react-hook-form keys array items by an id of its own. Renaming it leaves our
   * own question ids readable in `fields`, which is how a card knows whether its
   * question was generated or added by hand.
   */
  const { fields, insert, remove } = useFieldArray({
    control,
    name: 'questions',
    keyName: 'fieldId',
  });

  const applyQuiz = setQuiz;

  /*
   * The form is seeded from whatever quiz is in hand — on the first load and
   * again after a save. Reset rather than setValue: these are the values
   * "unsaved changes" and every dirty check are measured against from here on.
   */
  useEffect(() => {
    if (!quiz) return;
    reset({
      title: quiz.title ?? '',
      questions: (quiz.questions ?? []).map(toEditableQuestion),
    });
  }, [quiz, reset]);

  useEffect(() => {
    if (!needsValidation) return;
    setNeedsValidation(false);
    trigger();
  }, [needsValidation, trigger]);

  // Edits live in the form until Save is pressed, so leaving throws them away.
  const { isPromptOpen, guardNavigation, navigateAway, stay, leave } = useUnsavedChangesGuard({
    isDirty,
    backPath: '..',
  });

  /** Brings a question on screen: the page holding it, or the question itself on a phone. */
  const goToQuestion = (index) => {
    setPage(Math.floor(index / rowsPerPage));
    setActiveQuestion(index);
  };

  /** Turns to a question, and marks up what is missing there. */
  const jumpToQuestion = (index) => {
    goToQuestion(index);
    setNeedsValidation(true);
  };

  // Picked from the list at the foot of a phone screen, which replaces the card
  // on screen — so the page goes back to the top of the new one.
  const handleSelectQuestion = (index) => {
    goToQuestion(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddQuestion = (type) => {
    const index = addMenu.index + 1;
    insert(index, createEmptyQuestion(type));
    setAddMenu(null);
    // Inserting below the last question on a page pushes the new card onto the
    // next one, where the learner would never see it — and on a phone the new
    // question is the only one that would not be on screen at all.
    goToQuestion(index);
  };

  const handleDeleteQuestion = (index) => {
    remove(index);
    // Removing the last question on a page would otherwise strand the editor on
    // a page that no longer exists.
    const lastPage = Math.max(0, Math.ceil((fields.length - 1) / rowsPerPage) - 1);
    setPage((current) => Math.min(current, lastPage));
  };

  const handleSave = async ({ title, questions }) => {
    /*
     * react-hook-form has only checked the page on screen, so the quiz as a
     * whole is checked here — otherwise a question left blank on another page
     * would be saved that way.
     */
    const incompleteIndex = findIncompleteQuestion(questions);
    if (incompleteIndex >= 0) {
      jumpToQuestion(incompleteIndex);
      toast.error(QUIZ_INCOMPLETE_ERROR);
      return;
    }

    setIsSaving(true);
    try {
      applyQuiz(
        await putData(quizzes({ id: quizId, courseId }), {
          title,
          questions: questions.map(fromEditableQuestion),
        }),
      );
      toast.success('Quiz saved successfully.');
      /*
       * The editor stays where it is: saving is a checkpoint in a long piece of
       * work, not the end of it. The toast says it happened, and the line beside
       * the heading goes on saying when, after the toast has gone.
       */
      setSavedAt(new Date());
    } catch (err) {
      console.log(err);
      toast.error('Failed to save quiz. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  /*
   * The field holding the first error may well be one the learner cannot see, on
   * a page they are not on. Turn to it and say why nothing was saved.
   */
  const handleInvalid = (errors) => {
    const firstErrorIndex = (errors.questions ?? []).findIndex(Boolean);
    if (firstErrorIndex >= 0) jumpToQuestion(firstErrorIndex);
    toast.error(QUIZ_INCOMPLETE_ERROR);
  };

  const handleDelete = async () => {
    setIsDeleteOpen(false);
    setIsSaving(true);
    try {
      await deleteData(quizzes({ id: quizId, courseId }));
      toast.success('Practice Quiz deleted successfully.');
      navigateAway('../../..');
    } catch (err) {
      console.log(err);
      toast.error('Failed to delete quiz. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // As on the quiz itself: the frame stands and a block stands in for the panel.
  if (isLoading || loadError || !quiz) {
    return (
      <Box className={'mss__quizEditor' + (showBanner ? ' impersonating' : '')} id="quizEditor">
        <PageBreadcrumbs id="quizEditor__breadcrumbs" rootTo="../../.." isVisible={!loadError}>
          <BreadcrumbCrumb isLoading={!knownQuizTitle} to="..">
            <TruncatedTitle name={knownQuizTitle} isFocusable={false} variant="inherit" />
          </BreadcrumbCrumb>
          <Typography>Edit</Typography>
        </PageBreadcrumbs>

        {loadError ? (
          <AIResponseError
            message={loadError}
            onGoToDashboard={() => navigate('../../..', { relative: 'path' })}
          />
        ) : (
          /*
            Shaped like the form it stands in for — a title field and three
            question cards — rather than one grey slab, as the deck editor does.
          */
          <Box id="quizEditor__skeleton_loading" sx={{ mt: '1rem' }}>
            <Skeleton variant="text" width={80} height={24} />
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            {[1, 2, 3].map((row) => (
              <Skeleton
                key={row}
                variant="rectangular"
                height={120}
                sx={{ mb: 1, borderRadius: 1 }}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  const numberedQuestions = fields.map((field, index) => ({ field, index }));

  /*
   * A phone has room for one question at a time, so it takes the place of the
   * page of them and the pagination underneath goes with it. Deleting the last
   * question leaves the index past the end of the list, so it is clamped here
   * rather than chased through every handler that changes the list.
   */
  const activeIndex = Math.min(activeQuestion, Math.max(fields.length - 1, 0));
  const visibleQuestions = isNarrow
    ? numberedQuestions.slice(activeIndex, activeIndex + 1)
    : numberedQuestions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box className={'mss__quizEditor' + (showBanner ? ' impersonating' : '')} id="quizEditor">
      <Loader id="quizEditor" isOpen={isSaving} />

      <MssModal
        isOpen={isPromptOpen}
        onClose={stay}
        title="Unsaved Changes"
        id="quizEditor__modal_unsavedChanges"
        primaryButtonTitle="Stay"
        onPrimaryClick={stay}
        secondaryButtonTitle="Leave"
        onSecondaryClick={leave}
      >
        <Typography>Unsaved edits will not be applied. Do you wish to leave?</Typography>
      </MssModal>

      {isDeleteOpen && (
        <MssDelete
          id="quizEditor__confirmDelete"
          onToggle={() => setIsDeleteOpen(false)}
          onDelete={handleDelete}
          title="Delete Practice Quiz"
          resourceName={quiz.title}
          isLoading={isSaving}
          maxWidth="sm"
        />
      )}

      <PageBreadcrumbs
        id="quizEditor__breadcrumbs"
        rootTo="../../.."
        onRootClick={(event) => {
          event.preventDefault();
          guardNavigation('../../..');
        }}
      >
        <BreadcrumbCrumb
          to=".."
          onClick={(event) => {
            event.preventDefault();
            guardNavigation('..');
          }}
        >
          {/* The link is the tab stop; the name inside it does not need its own. */}
          <TruncatedTitle name={quiz.title} isFocusable={false} variant="inherit" />
        </BreadcrumbCrumb>
        <Typography>Edit</Typography>
      </PageBreadcrumbs>

      <Box className="editorContent" id="quizEditor__box">
        {/* Renders nothing once the quiz is ready, so the panel goes with it. */}
        {quiz.status !== RESOURCE_STATUS.COMPLETED && (
          <Box className="mss__box editorStatus">
            <QuizGenerationStatus
              quizId={quizId}
              courseId={courseId}
              status={quiz.status}
              errorCode={quiz.errorCode}
              onGenerated={applyQuiz}
              onGenerateNew={() => navigate('../..', { relative: 'path' })}
              onGoToDashboard={() => navigate('../../..', { relative: 'path' })}
            />
          </Box>
        )}

        {quiz.status === RESOURCE_STATUS.COMPLETED && (
          <>
            <Box className="resourceHeader" id="quizEditor__header">
              <Box className="resourceTitleGroup">
                <Box className="resourceTitle">
                  <Typography component="h1">Edit Practice Quiz</Typography>
                  <ResourceInfoPopover
                    id="quizEditor"
                    name={quiz.title}
                    nameLabel={RESOURCE_LABEL.QUIZ}
                    resources={quiz.resources}
                    lastEditedBy={quiz.lastEditedBy}
                    instructions={QUIZ_EDITOR_INSTRUCTIONS}
                  />
                </Box>
              </Box>

              <Box className="resourceActions resourceActions--stack">
                <UnsavedChangesWarning isDirty={isDirty} savedAt={savedAt} />
                <Button
                  variant="contained"
                  className="mss__button"
                  id="quizEditor__button_delete"
                  onClick={() => setIsDeleteOpen(true)}
                >
                  Delete Quiz
                </Button>
                <Button
                  variant="contained"
                  className="mss__button primary"
                  id="quizEditor__button_save"
                  disabled={isSaving || !isDirty}
                  onClick={handleSubmit(handleSave, handleInvalid)}
                >
                  Save
                </Button>
              </Box>
            </Box>

            {/* The header's copy is hidden on a phone, where it has no room. */}
            <UnsavedChangesWarning
              isDirty={isDirty}
              savedAt={savedAt}
              className="unsavedWarningMobile"
            />

            {/*
              One panel holds the whole editor below the heading — the deck
              editor frames its own the same way, with the rows inside standing
              out against it.
            */}
            <Box className="mss__box editorPanel" id="quizEditor__box_panel">
              <Box className="editorTitleField">
                <FormControlledTextField
                  id="quizEditor__input_title"
                  name="title"
                  control={control}
                  label="Quiz Title"
                  placeholder="Enter quiz title"
                  rules={{ validate: (value) => (value?.trim() ? true : 'Enter a quiz title.') }}
                  fullWidth
                />
              </Box>

              <Box className="editorQuestionList" id="quizEditor__questionList">
                {visibleQuestions.map(({ field, index }) => (
                  <QuestionEditor
                    key={field.fieldId}
                    id="quizEditor"
                    control={control}
                    index={index}
                    number={index + 1}
                    question={field}
                    canDelete={fields.length > 1}
                    onAdd={(event) => setAddMenu({ anchorEl: event.currentTarget, index })}
                    onDelete={() => handleDeleteQuestion(index)}
                  />
                ))}
              </Box>

              <Menu
                id="quizEditor__menu_addQuestion"
                anchorEl={addMenu?.anchorEl}
                open={!!addMenu}
                onClose={() => setAddMenu(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  id="quizEditor__menuItem_addTrueFalse"
                  onClick={() => handleAddQuestion(QUIZ_QUESTION_TYPE.TRUE_FALSE)}
                >
                  True/False
                </MenuItem>
                <MenuItem
                  id="quizEditor__menuItem_addMultipleChoice"
                  onClick={() => handleAddQuestion(QUIZ_QUESTION_TYPE.MULTIPLE_CHOICE)}
                >
                  Multiple Choice
                </MenuItem>
              </Menu>

              {!isNarrow && (
                <Box className="editorPagination" id="quizEditor__pagination">
                  <TablePagination
                    component="div"
                    count={fields.length}
                    page={page}
                    onPageChange={(_e, nextPage) => {
                      setPage(nextPage);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setPage(0);
                    }}
                    labelRowsPerPage="Rows per page:"
                  />
                </Box>
              )}
            </Box>

            {isNarrow && (
              <EditorQuestionListSheet
                id="quizEditor"
                control={control}
                isOpen={isQuestionListOpen}
                onClose={() => setIsQuestionListOpen(false)}
                activeIndex={activeIndex}
                onSelect={handleSelectQuestion}
              />
            )}
          </>
        )}
      </Box>

      {/*
        Outside the panel, so it pins itself to the foot of the screen rather
        than to the end of the question on screen.
      */}
      {isNarrow && quiz.status === RESOURCE_STATUS.COMPLETED && fields.length > 0 && (
        <QuizMobileBar
          id="quizEditor"
          index={activeIndex}
          total={fields.length}
          onChange={handleSelectQuestion}
          onOpenList={() => setIsQuestionListOpen(true)}
        />
      )}
    </Box>
  );
}

export default QuizEditor;
